import { Box, IconButton, Tooltip, tooltipClasses } from '@mui/material';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { Copy } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { cutUID } from '~/utils/string';

type IdFieldProps = {
    id: string;
    label?: string;
    typographyProps?: TypographyProps;
};

export default function IdField({ id, label, typographyProps }: IdFieldProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(id);
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={0.25}
            data-testid="id-field"
        >
            <SensitiveInfoTypography>
                <Typography
                    component="span"
                    fontSize={12}
                    lineHeight={1.2}
                    {...typographyProps}
                >{`${label ? `${label}:` : ''} ${cutUID(id)}`}</Typography>
            </SensitiveInfoTypography>
            <Tooltip
                PopperProps={{
                    sx: {
                        [`& .${tooltipClasses.tooltip}`]: {
                            maxWidth: 'none',
                        },
                    },
                }}
                title={showSensitiveInfo ? id : undefined}
                placement="right"
                disableHoverListener={!showSensitiveInfo}
                disableFocusListener={!showSensitiveInfo}
                disableTouchListener={!showSensitiveInfo}
            >
                <span>
                    <IconButton
                        size="small"
                        color="primary"
                        sx={{ my: -2 }}
                        onClick={copyToClipboard}
                        disabled={!showSensitiveInfo}
                        aria-label={intl.formatMessage({ id: 'copyId' })}
                        data-testid="copy-id-button"
                    >
                        <Copy size={10} />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}
